/**
 * **遮罩**：一层盖满整块画布的半透明块，顺便**吃掉所有落在它下面的点击**。
 *
 * 它存在的理由和 `screenHost.ts` 那个 scrim 一样：模态界面（菜单、确认框）
 * 必须"点底下的东西不生效"，而 Pixi 的命中测试是**从前往后**遍历显示树的 ——
 * 一块铺满的、`eventMode = 'static'` 的块就能把落下去的所有点击吃掉。
 *
 * ⚠️ 所以它必须住在**组件库**里，而不是各个屏自己 `new Graphics().on(...)`：
 * 「界面层不许自己挂指针事件」是设计规范里的一条硬规矩
 * （`test/ui/design.test.ts`）。屏只该调用组件。
 *
 * ⚠️ 浓度不是"压暗一点点"：有东西盖上来时，底下的牌桌要**明显退后**，
 * 玩家才知道"现在该看上面这一层"。所以默认 0.82（`screenHost.ts` 那个
 * 模态遮罩用的是 0.72 —— 它的面板更大、内容更多，可以浅一点）。
 */

import { Container, Graphics, Rectangle } from 'pixi.js';
import { BASE } from '../palette';
import type { Widget } from './base';

export interface ScrimSpec {
  readonly scale: number;
  readonly width: number;
  readonly height: number;
  /** 0–1。不给用默认值 */
  readonly alpha?: number;
  /** 点了这一层（也就是"点了面板外面"）。不给就是纯挡点击 */
  readonly onDismiss?: () => void;
}

export interface Scrim extends Widget {
  /** 画布尺寸变了就调它（视口会变，遮罩得跟着） */
  resize(width: number, height: number): void;
  setVisible(visible: boolean): void;
}

const DEFAULT_ALPHA = 0.82;

export function scrim(spec: ScrimSpec): Scrim {
  const container = new Container();
  container.label = 'scrim';
  const fill = new Graphics();
  container.addChild(fill);
  container.eventMode = 'static';

  const alpha = spec.alpha ?? DEFAULT_ALPHA;
  let width = spec.width;
  let height = spec.height;

  const paint = (): void => {
    fill.clear();
    fill.rect(0, 0, width, height).fill({ color: BASE.void, alpha });
    // ⚠️ hitArea 要显式给：Graphics 的实测边界在 clear 之后会塌成 0，
    //    而"吃掉点击"靠的正是它
    container.hitArea = new Rectangle(0, 0, width, height);
  };
  paint();

  if (spec.onDismiss !== undefined) {
    const dismiss = spec.onDismiss;
    container.on('pointertap', () => dismiss());
  }

  return {
    container,
    get width() {
      return width;
    },
    get height() {
      return height;
    },
    resize(nextWidth, nextHeight) {
      width = nextWidth;
      height = nextHeight;
      paint();
    },
    setVisible(visible) {
      container.visible = visible;
    },
  };
}
